<template>
  <div class="container py-0 catalog-page-shell mt-5">

    <!-- Mobile filter overlay -->
    <div 
      class="mobile-filter-overlay" 
      :class="{ 'active': showMobileFilters }"
      @click="closeMobileFilters"
    ></div>

    <div class="product-list-container py-0">
      <!-- Sidebar with filters -->
      <div class="sidebar catalog-sidebar p-0" :class="{ 'mobile-active': showMobileFilters }">
        <div class="filter-section px-3">
          <div class="catalog-mobile-bar d-lg-none">
            <span class="catalog-mobile-bar-title">Filtros</span>
            
            <button
              type="button"
              class="close-filters"
              aria-label="Cerrar filtros"
              @click="closeMobileFilters"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
          <div class="sidebar-header-block pt-3">
            <div class="d-flex align-items-center justify-content-between">
              <h1 class="catalog-heading font-good-brush">CATÁLOGO</h1>
              <button v-if="hasActiveFilters" @click="clearAllFilters" class="clear-all-btn">
                <i class="fas fa-times me-1"></i>Limpiar
              </button>
            </div>
            <div class="products-found-text">
              <span class="result-count">{{ totalItems }}</span> productos encontrados
            </div>
          </div>
          
          <!-- Applied Filters Tags -->
          <div v-if="hasActiveFilters" class="applied-filters">
            <div class="d-flex flex-wrap gap-2 align-items-center">
              
              <!-- Category filters -->
              <span 
                v-for="category in selectedCategories" 
                :key="category"
                class="filter-badge text-uppercase"
              >
                {{ getCategoryLabel(category) }}
                <button 
                  class="filter-remove-btn" 
                  @click="removeFilter('category', category)"
                  :disabled="isLoading"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
              
              <!-- On Sale filter -->
              <span 
                v-if="showOnSaleOnly"
                class="filter-badge filter-badge-sale"
              >
                En oferta
                <button 
                  class="filter-remove-btn" 
                  @click="removeFilter('sale')"
                  :disabled="isLoading"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
              
              <!-- Search filter -->
              <span 
                v-if="searchQuery.trim()"
                class="filter-badge filter-badge-search"
              >
                "{{ searchQuery }}"
                <button 
                  class="filter-remove-btn" 
                  @click="removeFilter('search')"
                  :disabled="isLoading"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>

              <span 
                v-if="selectedBrand"
                class="filter-badge filter-badge-brand"
              >
                Marca: {{ selectedBrand }}
                <button 
                  class="filter-remove-btn" 
                  @click="removeFilter('brand')"
                  :disabled="isLoading"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
            </div>
          </div>

            <!-- Search box with icon -->
          <div class="search-box">
            <i class="fas fa-search text-secondary search-icon"></i>
            <input  
              type="text" 
              v-model="searchQuery"
              placeholder="Buscar dentro del catálogo (nombre, marca, categoría)..."
              class="search-input text-dark rounded-0"
              @input="handleSearch"
            >
            <button 
              v-if="searchQuery"
              @click="clearSearch" 
              class="clear-search"
            >
              <i class="fas fa-times"></i>
            </button>          </div>

          <!-- Skeleton for Categories when loading -->
          <div v-if="isLoadingCategories" class="categories-section">
            <div class="category-skeleton">
              <div v-for="i in 6" :key="i" class="skeleton-group">
                <div class="skeleton-nav-row">
                  <div class="skeleton-nav-label skeleton-shimmer"></div>
                  <div class="skeleton-nav-meta">
                    <div class="skeleton-nav-count skeleton-shimmer"></div>
                    <div class="skeleton-nav-chevron skeleton-shimmer"></div>
                  </div>
                </div>
                <div v-if="i % 2 === 0" class="skeleton-subcategories">
                  <div v-for="j in 2" :key="j" class="skeleton-sub-row">
                    <div class="skeleton-sub-check skeleton-shimmer"></div>
                    <div class="skeleton-sub-label skeleton-shimmer"></div>
                    <div class="skeleton-sub-count skeleton-shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Categorías: dropdowns con subcategorías y marcas -->
          <div v-else class="categories-section" @click.stop>
            <!-- Marcas (todas) -->
            <div 
              class="catalog-nav-item" 
              :class="{ 'is-open': openMenuId === 'marcas', 'has-active': !!selectedBrand && !selectedCategories.length }"
            >
              <button
                type="button"
                class="catalog-nav-trigger text-uppercase"
                @click.stop="toggleMenu('marcas')"
              >
                <span>Marcas</span>
                <i class="fas fa-chevron-down" :class="{ rotate: openMenuId === 'marcas' }"></i>
              </button>
              <div v-show="openMenuId === 'marcas'" class="catalog-submenu">
                <div class="catalog-submenu-scroll subtle-scroll">
                  <button
                    v-for="b in catalogAllBrands"
                    :key="'all-' + b.name"
                    type="button"
                    class="catalog-submenu-row"
                    :class="{ active: selectedBrand === b.name && !selectedCategories.length }"
                    @click="applyBrandOnly(b.name)"
                  >
                    <span>{{ b.name }}</span>
                    <span class="catalog-submenu-count">{{ b.count }}</span>
                  </button>
                  <p v-if="!catalogAllBrands.length" class="catalog-submenu-empty text-muted small mb-0">Sin marcas</p>
                </div>
              </div>
            </div>

            <div 
              v-for="group in categoryGroups" 
              :key="group.id || group.main" 
              class="catalog-nav-item"
              :class="{ 
                'is-open': openMenuId === navKeyForGroup(group),
                'has-active': isCategoryGroupActive(group)
              }"
            >
              <button
                type="button"
                class="catalog-nav-trigger text-uppercase"
                @click.stop="toggleMenu(navKeyForGroup(group))"
              >
                <span>{{ group.main }}</span>
                <span v-if="group.totalCount != null" class="catalog-nav-trigger-count">{{ group.totalCount }}</span>
                <i class="fas fa-chevron-down" :class="{ rotate: openMenuId === navKeyForGroup(group) }"></i>
              </button>
              <div v-show="openMenuId === navKeyForGroup(group)" class="catalog-submenu">
                <div class="catalog-submenu-scroll subtle-scroll">
                  <template v-if="group.sub?.length">
                    <p class="catalog-submenu-heading">Subcategorías</p>
                    <button
                      v-for="sub in group.sub"
                      :key="sub.id || sub.value"
                      type="button"
                      class="catalog-submenu-row"
                      :class="{
                        active: isSubcategoryActive(sub.value) && !selectedBrand,
                        disabled: sub.count === 0,
                        loading: loadingCategory === sub.value
                      }"
                      :disabled="!sub.hasProducts || loadingCategory === sub.value"
                      @click="sub.hasProducts ? selectSubcategoryFromMenu(sub) : null"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="sub-check-box">
                          <i v-if="isSubcategoryActive(sub.value) && !selectedBrand" class="fas fa-check"></i>
                          <i v-else-if="loadingCategory === sub.value" class="fas fa-spinner fa-spin"></i>
                        </span>
                        {{ sub.label }}
                      </span>
                      <span class="catalog-submenu-count">{{ sub.count }}</span>
                    </button>
                  </template>

                  <template v-if="group.brands?.length">
                    <p class="catalog-submenu-heading">Marcas</p>
                    <button
                      v-for="b in group.brands"
                      :key="'gb-' + group.main + '-' + b.name"
                      type="button"
                      class="catalog-submenu-row"
                      :class="{ active: selectedBrand === b.name && isParentScopeActive(group.main) }"
                      @click="applyBrandInCategory(b.name, group.main)"
                    >
                      <span>{{ b.name }}</span>
                      <span class="catalog-submenu-count">{{ b.count }}</span>
                    </button>
                  </template>

                  <template v-if="!group.sub?.length && !group.brands?.length">
                    <button
                      type="button"
                      class="catalog-submenu-row"
                      :class="{ active: isSubcategoryActive(group.main) && !selectedBrand }"
                      @click="selectSubcategoryFromMenu({ value: group.main, label: group.main, hasProducts: true })"
                    >
                      <span>Ver todo</span>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Mobile filter actions -->
        <div class="mobile-filter-actions d-lg-none">
          <button class="btn btn-outline-secondary" @click="clearAllFilters">
            Limpiar filtros
          </button>
          <button class="btn btn-primary" @click="applyFilters">
            Ver {{ totalItems }} resultados
          </button>
        </div>
      </div>      <!-- Products grid con paginación -->
      <div class="products-section pt-3">
        <!-- Products header with actions -->
        <div class="products-header-mobile pt-3 mb-3  mb-0 pb-0 d-flex justify-content-lg-start justify-content-between align-items-center px-0 flex-wrap gap-2">
          <div class="sidebar-header-block flex-grow-1" v-if="isMobile">
            <div class="d-flex align-items-center justify-content-between">
              <h1 class="catalog-heading font-good-brush">CATÁLOGO</h1>
              <button v-if="hasActiveFilters" @click="clearAllFilters" class="clear-all-btn">
                <i class="fas fa-times me-1"></i>Limpiar
              </button>
            </div>
            <div class="products-found-text">
              <span class="result-count">{{ totalItems }}</span> productos encontrados
            </div>
          </div>
          <div class="d-flex align-items-center justify-content-start gap-2"></div>
        </div>

        <!-- Categorías visibles en móvil (el panel lateral quedaba oculto por CSS en ≤768px) -->
        <div class="catalog-mobile-category-rail d-lg-none px-0" aria-label="Categorías del catálogo">
          <div v-if="isLoadingCategories" class="catalog-mobile-category-skeleton">
            <div v-for="i in 6" :key="'cat-skel-' + i" class="catalog-mobile-category-skeleton-pill"></div>
          </div>
          <div v-else class="catalog-mobile-category-scroll subtle-scroll">
            <button
              type="button"
              class="catalog-mobile-category-pill catalog-mobile-category-pill--icon"
              :aria-label="sortButtonLabel"
              @click="cycleSortOrder"
            >
              <i class="fas" :class="sortIconClass" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="catalog-mobile-category-pill"
              :class="{
                active:
                  !selectedCategories.length &&
                  !selectedBrand &&
                  !showOnSaleOnly
              }"
              @click="resetMobileCatalogScope"
            >
              Todas
            </button>
            <button
              type="button"
              class="catalog-mobile-category-pill"
              :class="{ active: !!selectedBrand && !selectedCategories.length }"
              @click="openMobileFiltersMarcas"
            >
              Marcas
            </button>
            <button
              v-for="group in categoryGroups"
              :key="'mcat-' + (group.id || group.main)"
              type="button"
              class="catalog-mobile-category-pill"
              :class="{ active: isCategoryGroupActive(group) }"
              @click="onMobileCategoryPillClick(group)"
            >
              {{ group.main }}
            </button>
          </div>
        </div>

        <!-- Overlay de carga -->
        <div class="loading-overlay" v-if="isLoading && !initialLoad && products.length > 0">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Cargando productos...</span>
          </div>
        </div>

        <div class="products-grid pt-0">
          <!-- Loading state - Show skeleton for initial load OR when products are cleared for category change -->
          <template v-if="isLoading && (initialLoad || products.length === 0)">
            <ProductSkeleton 
              v-for="n in pageSize" 
              :key="`skeleton-${n}`"
              class="product-skeleton-item"
            />
          </template>

          <!-- No products state - Only show when not loading and no products found -->
          <div v-else-if="!isLoading && !products.length" class="container-fluid d-flex justify-content-center align-items-center" key="no-products">
              <div class="container-fluid w-100 d-flex flex-column align-items-center text-center py-5">
                <i class="fas fa-search mb-3 display-1 text-primary"></i>
                <h3 class="fw-semibold text-white-50">No se encontraron productos</h3>
                <p class="text-muted mb-4">
                  No hay productos que coincidan con los filtros seleccionados.
                  Intenta cambiar los criterios de búsqueda.
                </p>
                <div>
                  <button @click="clearAllFilters" class="btn btn-primary">
                    <i class="fas fa-times me-2"></i>
                    Limpiar filtros
                  </button>
                </div>
              </div>
         </div>
          
          <!-- Products - Usar productos estables con key única -->
          <template v-else>
            <ProductCard
              v-for="product in products"
              :key="`product-${product.id}-${currentPage}`"
              :product="product"
            />
          </template>
        </div>
        
        <!-- Paginación -->
        <div v-if="totalPages > 1" class="pagination-container mt-5">
          <button 
            class="pagination-btn" 
            @click="changePage(1)" 
            :disabled="currentPage === 1"
          >
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button 
            class="pagination-btn" 
            @click="changePage(currentPage - 1)" 
            :disabled="currentPage === 1"
          >
            <i class="fas fa-angle-left"></i>
          </button>
          
          <button 
            v-for="page in displayedPages" 
            :key="page"
            class="pagination-btn"
            :class="{ active: page === currentPage }"
            @click="typeof page === 'number' ? changePage(page) : null"
            :disabled="typeof page !== 'number'"
          >
            {{ page }}
          </button>
          
          <button 
            class="pagination-btn" 
            @click="changePage(currentPage + 1)" 
            :disabled="currentPage === totalPages"
          >
            <i class="fas fa-angle-right"></i>
          </button>
          <button 
            class="pagination-btn" 
            @click="changePage(totalPages)" 
            :disabled="currentPage === totalPages"
          >
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getProducts } from '@/services/products'
import { getCatalogFacets } from '@/services/categories'
import { debounce } from '@/utils/text'
import ProductSkeleton from '@/components/ProductSkeleton.vue'
import ProductCard from '@/components/ProductCard.vue'
import ScrollReveal from 'scrollreveal'

const props = defineProps({
  isProductsPage: {
    type: Boolean,
    default: true
  }
})

const router = useRouter()
const route = useRoute()

const isMobile = ref(window.innerWidth < 980)

// Estado base
const products = ref([])
const isLoading = ref(true)
const initialLoad = ref(true)
const currentPage = ref(1)
const totalItems = ref(0)
const pageSize = ref(props.isProductsPage ? 12 : 9)
const sortOrder = ref('')
const searchQuery = ref('')
const selectedCategories = ref([])
const showMobileFilters = ref(false)
const loadingError = ref(null)
const isLoadingCategories = ref(true) // Add this line for categories loading state
const showOnSaleOnly = ref(false) // Nuevo estado para filtrar productos en oferta
const loadingCategory = ref(null) // Track which category is being loaded
const selectedBrand = ref('')
const openMenuId = ref(null)
const catalogAllBrands = ref([])

const sortIconClass = computed(() => {
  if (sortOrder.value === 'price_asc') return 'fa-sort-amount-up-alt'
  if (sortOrder.value === 'price_desc') return 'fa-sort-amount-down'
  return 'fa-sort'
})

const sortButtonLabel = computed(() => {
  if (sortOrder.value === 'price_asc') return 'Ordenar: menor precio'
  if (sortOrder.value === 'price_desc') return 'Ordenar: mayor precio'
  return 'Ordenar: sin filtro'
})

const firstQueryValue = (value) => {
  if (Array.isArray(value)) return value[0]
  return value
}

const parseCategoryQuery = (query) => {
  const rawCategory =
    firstQueryValue(query.category_name) ||
    firstQueryValue(query.categories) ||
    firstQueryValue(query.category)

  if (!rawCategory) return []

  return String(rawCategory)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

const parseSaleQuery = (query) => {
  return firstQueryValue(query.sale) === 'true' || firstQueryValue(query.on_sale) === 'true'
}

// Nuevo estado para precargar productos
const preloadedProducts = ref({})
const isPreloading = ref(false)

// Pagination state and computed props
const pagination = ref({
  count: 0,
  next: null,
  previous: null
});

const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

const displayedPages = computed(() => {
  const range = 2;
  const pages = [];
  const start = Math.max(1, currentPage.value - range);
  const end = Math.min(totalPages.value, currentPage.value + range);

  // Siempre agregar la primera página si hay más de una página
  if (totalPages.value > 1) {
    if (start > 1) pages.push(1);
    if (start > 2) pages.push('...');
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    if (end < totalPages.value - 1) pages.push('...');
    if (end < totalPages.value) pages.push(totalPages.value);
  }
  
  return pages;
})


// Computed para verificar si hay filtros activos
const hasActiveFilters = computed(() => {
  return selectedCategories.value.length > 0 || 
         showOnSaleOnly.value || 
         searchQuery.value.trim().length > 0 ||
         !!selectedBrand.value
})

// Función para obtener el label de la categoría
const getCategoryLabel = (categoryValue) => {
  // Buscar en categoryGroups el label correspondiente
  for (const group of categoryGroups.value) {
    if (group.main === categoryValue) {
      return group.main.toUpperCase()
    }
    if (group.sub) {
      const found = group.sub.find(sub => sub.value === categoryValue)
      if (found) {
        return found.label.toUpperCase()
      }
    }
  }
  return categoryValue.toUpperCase()
}

// Función para eliminar un filtro específico
const removeFilter = async (filterType, value = null) => {
  isLoading.value = true
  products.value = []
  preloadedProducts.value = {}
  currentPage.value = 1
  
  if (filterType === 'category' && value) {
    selectedCategories.value = selectedCategories.value.filter(cat => cat !== value)
  } else if (filterType === 'sale') {
    showOnSaleOnly.value = false
  } else if (filterType === 'search') {
    searchQuery.value = ''
  } else if (filterType === 'brand') {
    selectedBrand.value = ''
  }
  
  await updateRouteWithFilters(1)
  
  // Siempre usar loadProducts que maneja ambos filtros (categoría + ofertas)
  await loadProducts(1, true)
}


const categoryGroups = ref([])

const transformFacetsToGroups = (parents) => {
  if (!Array.isArray(parents)) return []
  return parents.map((p) => ({
    id: p.id,
    main: p.name,
    totalCount: p.product_count ?? 0,
    brands: p.brands || [],
    sub: (p.subcategories || []).map((s) => ({
      id: s.id,
      value: s.name,
      label: s.name,
      count: s.product_count ?? 0,
      hasProducts: (s.product_count || 0) > 0,
      brands: s.brands || []
    }))
  })).sort((a, b) => (b.totalCount || 0) - (a.totalCount || 0))
}

const loadCategories = async () => {
  try {
    isLoadingCategories.value = true
    const data = await getCatalogFacets()
    catalogAllBrands.value = data.all_brands || []
    categoryGroups.value = transformFacetsToGroups(data.parents || [])
  } catch (error) {
    console.error('Error loading catalog facets:', error)
    catalogAllBrands.value = []
    categoryGroups.value = []
  } finally {
    isLoadingCategories.value = false
  }
}

const navKeyForGroup = (group) => `p-${group.id ?? group.main}`

const toggleMenu = (id) => {
  openMenuId.value = openMenuId.value === id ? null : id
}

const isParentScopeActive = (parentMain) => {
  if (!selectedBrand.value || selectedCategories.value.length !== 1) return false
  const group = categoryGroups.value.find((g) => g.main === parentMain)
  if (!group) return false
  const cat = selectedCategories.value[0]
  if (cat === parentMain) return true
  return !!(group.sub && group.sub.some((s) => s.value === cat))
}

const isCategoryGroupActive = (group) => {
  if (!selectedCategories.value.length && !selectedBrand.value) return false
  const cat = selectedCategories.value[0]
  if (cat === group.main) return true
  return !!(group.sub && group.sub.some((s) => s.value === cat))
}

const closeMobileFilters = () => {
  showMobileFilters.value = false
  document.body.classList.remove('filters-active')
}

const openMobileFilters = () => {
  showMobileFilters.value = true
  document.body.classList.add('filters-active')
}

const applyBrandOnly = async (brandName) => {
  openMenuId.value = null
  loadingCategory.value = brandName
  showOnSaleOnly.value = false
  isLoading.value = true
  products.value = []
  selectedCategories.value = []
  selectedBrand.value = brandName
  currentPage.value = 1
  preloadedProducts.value = {}
  showMobileFilters.value = false
  closeMobileFilters()
  await loadProducts(1, true)
  await updateRouteWithFilters(1)
  loadingCategory.value = null
}

const applyBrandInCategory = async (brandName, parentMain) => {
  const group = categoryGroups.value.find((g) => g.main === parentMain)
  let categoryForApi = parentMain
  if (group?.sub?.length && selectedCategories.value.length === 1) {
    const cur = selectedCategories.value[0]
    if (group.sub.some((s) => s.value === cur)) {
      categoryForApi = cur
    }
  }
  openMenuId.value = null
  loadingCategory.value = brandName
  showOnSaleOnly.value = false
  isLoading.value = true
  products.value = []
  selectedCategories.value = [categoryForApi]
  selectedBrand.value = brandName
  currentPage.value = 1
  preloadedProducts.value = {}
  showMobileFilters.value = false
  closeMobileFilters()
  await loadProducts(1, true)
  await updateRouteWithFilters(1)
  loadingCategory.value = null
}

const selectSubcategoryFromMenu = async (sub) => {
  openMenuId.value = null
  selectedBrand.value = ''
  await selectSubcategory(sub)
}

const onDocumentClickCloseMenus = (e) => {
  if (e.target.closest && e.target.closest('.catalog-nav-item')) return
  openMenuId.value = null
}

const onResizeCatalogLayout = () => {
  isMobile.value = window.innerWidth < 980
}

/** Vista móvil: quitar solo categoría / marca / oferta (mantiene búsqueda si hay). */
const resetMobileCatalogScope = async () => {
  closeMobileFilters()
  selectedCategories.value = []
  selectedBrand.value = ''
  showOnSaleOnly.value = false
  currentPage.value = 1
  preloadedProducts.value = {}
  isLoading.value = true
  products.value = []
  await updateRouteWithFilters(1)
  await loadProducts(1, true)
}

const openMobileFiltersMarcas = () => {
  openMenuId.value = 'marcas'
  openMobileFilters()
}

const onMobileCategoryPillClick = async (group) => {
  openMenuId.value = null
  await selectSubcategoryFromMenu({
    value: group.main,
    label: group.main,
    hasProducts: true
  })
}

// Función para inicializar ScrollReveal para animaciones
const initializeScrollReveal = () => {
  if (typeof ScrollReveal !== 'undefined') {
    // Configurar efectos para los elementos de la página
    ScrollReveal().reveal('.product-card', {
      duration: 800,
      origin: 'bottom',
      distance: '20px',
      delay: 100,
      interval: 100,
      reset: false,
      easing: 'cubic-bezier(0.5, 0, 0, 1)',
      viewFactor: 0.2,
      mobile: true,
      beforeReveal: (el) => {
        el.style.opacity = '1';
      }
    });

    // Animación para los encabezados
    ScrollReveal().reveal('.display-6', { 
      duration: 800,
      delay: 100,
      distance: '20px',
      origin: 'left'
    });

    // Animación para el área de filtros
    ScrollReveal().reveal('.filter-section', { 
      duration: 800,
      delay: 200,
      distance: '20px',
      origin: 'left'
    });
    
    // Animación para la paginación
    ScrollReveal().reveal('.pagination-container', { 
      duration: 800,
      delay: 300,
      distance: '20px',
      origin: 'bottom'
    });
  }
}

const applySort = () => {
  preloadedProducts.value = {}
  currentPage.value = 1
  updateRouteWithFilters(1)
  loadProducts(1)
}

const cycleSortOrder = () => {
  if (!sortOrder.value) {
    sortOrder.value = 'price_asc'
  } else if (sortOrder.value === 'price_asc') {
    sortOrder.value = 'price_desc'
  } else {
    sortOrder.value = ''
  }
  applySort()
}

// Modificar loadProducts para mantener el ordenamiento
const loadProducts = async (page = 1, preloadNextPage = true) => {
  try {
    // Set loading state immediately for category changes
    const isInitialLoad = !products.value.length;
    if (isInitialLoad || selectedCategories.value.length > 0 || searchQuery.value?.trim() || selectedBrand.value) {
      isLoading.value = true;
    }
    
    loadingError.value = null;
    
    page = Math.max(1, parseInt(page) || 1);
    
    // Check preloaded products
    if (preloadedProducts.value[page]) {
      products.value = [...preloadedProducts.value[page].results];
      pagination.value = {
        count: preloadedProducts.value[page].count,
        next: preloadedProducts.value[page].next,
        previous: preloadedProducts.value[page].previous
      };
      totalItems.value = preloadedProducts.value[page].count;
      currentPage.value = page;
      
      delete preloadedProducts.value[page];
      isLoading.value = false;
      
      if (preloadNextPage && pagination.value.next) {
        setTimeout(() => preloadPageProducts(page + 1), 300);
      }
      
      // Solo reiniciar las animaciones de los productos y paginación
      setTimeout(() => {
        ScrollReveal().reveal('.products-grid .product-card');
        ScrollReveal().reveal('.pagination-container');
      }, 100);
      
      return;
    }

    const params = {
      page: page,
      page_size: pageSize.value,
      is_available: true
    };

    // Add search param if it exists
    if (searchQuery.value?.trim()) {
      params.search = searchQuery.value.trim();
      console.log('Searching for:', searchQuery.value);
    }

    // Add category param if it exists - use category_name as the param name
    if (selectedCategories.value.length > 0) {
      params.category_name = selectedCategories.value[0];
      console.log('Filtering by category:', selectedCategories.value[0]);
    }
    if (selectedBrand.value) {
      params.brand = selectedBrand.value;
    }
      // Add on_sale filter if it's active
    if (showOnSaleOnly.value) {
      params.on_sale = true;
      console.log('Filtering by products on sale using parameter');
    }

    if (sortOrder.value === 'price_asc' || sortOrder.value === 'price_desc') {
      params.ordering = sortOrder.value;
    }

    console.log('Making API request with params:', params);

    const response = await getProducts(params);
    console.log('Products received:', response.results?.length);
    
    if (!response || !Array.isArray(response.results)) {
      throw new Error('Formato de respuesta inválido');
    }
    
    products.value = response.results;
    
    pagination.value = {
      count: response.count,
      next: response.next,
      previous: response.previous
    };
    totalItems.value = response.count;
    currentPage.value = page;
    
    if (preloadNextPage && response.next) {
      setTimeout(() => preloadPageProducts(page + 1), 300);
    }
    
  } catch (error) {
    console.error('Error al cargar productos:', error);
    loadingError.value = error.message || 'Error al cargar los productos. Por favor intenta nuevamente.';
    products.value = [];
    totalItems.value = 0;
  } finally {
    setTimeout(() => {
      isLoading.value = false;
      initialLoad.value = false;
      
      // Solo reiniciar las animaciones de los productos y paginación
      ScrollReveal().reveal('.products-grid .product-card');
    }, 300);
  }
};

// Update preloadPageProducts method to also use category_name
const preloadPageProducts = async (page) => {
  if (isPreloading.value || preloadedProducts.value[page]) {
    return;
  }
  
  try {
    isPreloading.value = true;
    
    const params = {
      page: page,
      page_size: pageSize.value,
      is_available: true
    };
    
    if (searchQuery.value?.trim()) {
      params.search = searchQuery.value.trim();
    }
    
    if (selectedCategories.value.length > 0) {
      params.category_name = selectedCategories.value[0];
    }
    if (selectedBrand.value) {
      params.brand = selectedBrand.value;
    }
    
    // Añadir filtro de ofertas para la precarga
    if (showOnSaleOnly.value) {
      params.on_sale = true;
    }
    
    const response = await getProducts(params);
    
    if (response && Array.isArray(response.results)) {
      preloadedProducts.value[page] = response;
    }
  } catch (error) {
    console.error('Error al precargar página', page, error);
  } finally {
    isPreloading.value = false;
  }
};

// Función específica para cargar productos en oferta cuando se selecciona la categoría "En oferta" del sidebar
// Esta función solo se usa cuando se hace clic en la categoría especial "En oferta", NO para el checkbox
const loadProductsOnSale = async (page = 1) => {
  try {
    // Loading state is already set in selectSubcategory, but ensure it's set here too
    if (!isLoading.value) {
      isLoading.value = true;
    }
    
    loadingError.value = null;
    showOnSaleOnly.value = true; // Marcamos que estamos viendo ofertas
    // Solo limpiamos categorías cuando se hace clic en "En oferta" del sidebar
    // (comportamiento: mostrar TODAS las ofertas, sin filtro de categoría)
    selectedCategories.value = [];
    selectedBrand.value = '';
    
    console.log(`Loading products on sale, page ${page}`);
    
    // Usar loadProducts con parámetro on_sale en lugar del endpoint especializado
    // Esto permitirá que funcione con paginación normal
    await loadProducts(page, true);
    
    // Actualizar el contador de productos en oferta en la categoría especial
    if (page === 1) {
      await updateRouteWithFilters(1);
    }
    
  } catch (error) {
    console.error('Error cargando productos en oferta:', error);
    loadingError.value = 'Error al cargar productos en oferta. Por favor intenta nuevamente.';
    products.value = [];
  } finally {
    isLoading.value = false;
    initialLoad.value = false;
  }
};

// Add the changePage function
const changePage = async (page) => {
  // Validate page number
  if (page < 1 || page > totalPages.value || page === currentPage.value) {
    return;
  }

  // Show loading state for page changes
  isLoading.value = true;
  products.value = []; // Clear products to show skeletons

  // Clear preloaded products for new page
  preloadedProducts.value = {};
  
  // Update current page
  currentPage.value = page;
  
  // Update URL with new page
  await updateRouteWithFilters(page);
  
  // Siempre usar loadProducts que maneja ambos filtros (categoría + ofertas)
  await loadProducts(page, true);
  
  // Scroll to top smoothly
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// Update selectSubcategory to properly handle category selection
const selectSubcategory = async (sub) => {
  console.log('Selected category:', sub);
  
  // Set loading category indicator
  loadingCategory.value = sub.value;
  
  // Si es la categoría especial "En oferta"
  if (sub.value === 'en-oferta') {
    // Show loading state immediately
    isLoading.value = true;
    products.value = []; // Clear products to show skeletons
    
    // Desactivar cualquier otra categoría seleccionada
    selectedCategories.value = [];
    selectedBrand.value = '';
    // Activar la casilla de verificación "Mostrar solo ofertas"
    showOnSaleOnly.value = true;
    // Cargar productos en oferta usando la ruta específica
    await loadProductsOnSale(1);
    loadingCategory.value = null; // Clear loading indicator
    return;
  }
  
  // Para otras categorías, comportamiento normal
  // Show loading state immediately
  isLoading.value = true;
  products.value = []; // Clear products to show skeletons
  selectedBrand.value = '';
  
  // Toggle category selection
  if (selectedCategories.value.includes(sub.value)) {
    selectedCategories.value = [];
  } else {
    selectedCategories.value = [sub.value];
  }
  
  // No reseteamos showOnSaleOnly para permitir acumular filtros (categoría + ofertas)
  
  showMobileFilters.value = false;
  currentPage.value = 1;
  preloadedProducts.value = {};
  
  console.log('Active categories after selection:', selectedCategories.value);
  
  // Force reload products with new category
  await loadProducts(1, true);
  
  // Update URL to reflect category change
  await updateRouteWithFilters(1);
  
  // Clear loading indicator
  loadingCategory.value = null;
};

// Update the handleSearch function to use the debounce function directly
const handleSearch = debounce(() => {
  console.log('Search query changed:', searchQuery.value);
  
  // Show loading state immediately for search
  isLoading.value = true;
  products.value = []; // Clear products to show skeletons
  
  preloadedProducts.value = {};
  currentPage.value = 1;
  loadProducts(1);
}, 500);

// Add the missing updateRouteWithFilters function
const updateRouteWithFilters = async (page = currentPage.value) => {
  const query = {
    page: page
  };
  
  if (searchQuery.value?.trim()) {
    query.search = searchQuery.value.trim();
  }
  
  if (selectedCategories.value.length) {
    query.category_name = selectedCategories.value.join(',');
  }
  
  if (showOnSaleOnly.value) {
    query.sale = 'true';
  }

  if (selectedBrand.value) {
    query.brand = selectedBrand.value;
  }
  
  console.log('Updating route with filters:', query);
  
  // Use router properly
  try {
    await router.push({ query });
  } catch (err) {
    if (err.name !== 'NavigationDuplicated') {
      console.error('Navigation error:', err);
    }
  }
};

// Add clearSearch function to make use of handleSearch
const clearSearch = () => {
  searchQuery.value = '';
  handleSearch();
};

// Método para limpiar todos los filtros - Conservamos solo esta implementación
const clearAllFilters = () => {
  // Limpiar productos precargados cuando se limpian los filtros
  preloadedProducts.value = {}
  
  searchQuery.value = ''
  selectedCategories.value = []
  currentPage.value = 1
  closeMobileFilters()
  showOnSaleOnly.value = false // Restablecer filtro de ofertas
  selectedBrand.value = ''
  updateRouteWithFilters(1)
  loadProducts(1)
}

const applyFilters = () => {
  // Limpiar productos precargados cuando se aplican los filtros
  preloadedProducts.value = {}
  
  closeMobileFilters()
  
  loadProducts(currentPage.value)
}


// Add this function to the setup section, after the existing refs and before other functions
const isSubcategoryActive = (subcategory) => {
  // Verificar si es la categoría especial "en-oferta"
  if (subcategory === 'en-oferta' || subcategory === 'En oferta') {
    return parseSaleQuery(route.query);
  }
  
  // Para otras categorías, verificar si están en el array de categorías seleccionadas
  return selectedCategories.value.includes(subcategory);
};

// Watchers
// Mejorar el watch de la ruta para manejar mejor los cambios
watch(() => route.query, (newQuery, oldQuery) => {
  // Evitar recargas innecesarias si no hay cambios reales
  if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return
  
  const newPage = parseInt(newQuery.page) || 1
  const newSearch = firstQueryValue(newQuery.search) || ''
  const newCategories = parseCategoryQuery(newQuery)
  // Leer estado de productos en oferta desde URL
  const newSaleFilter = parseSaleQuery(newQuery)
  const newBrand = firstQueryValue(newQuery.brand) || ''
  
  // Solo actualizar valores si han cambiado para evitar loops infinitos
  let needsReload = false
  
  if (searchQuery.value !== newSearch) {
    searchQuery.value = newSearch
    // Limpiar productos precargados cuando cambia la búsqueda
    preloadedProducts.value = {}
    needsReload = true
  }
  
  if (JSON.stringify(selectedCategories.value) !== JSON.stringify(newCategories)) {
    selectedCategories.value = [...newCategories]
    // Limpiar productos precargados cuando cambian las categorías
    preloadedProducts.value = {}
    needsReload = true
    
    // The parent category expansion logic is now handled in loadCategories
    // after categoryGroups are populated
  }

  if (selectedBrand.value !== newBrand) {
    selectedBrand.value = newBrand
    preloadedProducts.value = {}
    needsReload = true
  }

  if (showOnSaleOnly.value !== newSaleFilter) {
    console.log('Sale filter changed from URL:', newSaleFilter);
    showOnSaleOnly.value = newSaleFilter
    preloadedProducts.value = {}
    needsReload = true
  }
  
  if (currentPage.value !== newPage) {
    currentPage.value = newPage
    needsReload = true
  }
  
  // Solo recargar si algo cambió realmente
  if (needsReload) {
    // Siempre usar loadProducts que maneja ambos filtros (categoría + ofertas)
    loadProducts(currentPage.value);
  }
}, { immediate: true, deep: true })

onMounted(() => {
  initialLoad.value = true
  loadCategories()
  
  // Verificar si estamos en la vista de ofertas según la URL
  if (parseSaleQuery(route.query)) {
    // Cargar directamente productos en oferta
    loadProductsOnSale();
  } else {
    // Carga normal de productos
    const page = parseInt(route.query.page) || 1;
    loadProducts(page);
  }
  
  document.addEventListener('click', onDocumentClickCloseMenus)
  window.addEventListener('resize', onResizeCatalogLayout)

  // Inicializar ScrollReveal
  initializeScrollReveal()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClickCloseMenus)
  window.removeEventListener('resize', onResizeCatalogLayout)
})
</script>


<style scoped>
.container {
  overflow-x: hidden;
}

.catalog-page-shell {
  background: #000000;
  color: #ffffff;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 576px) {
  .container {
    padding-right: 0;
    padding-left: 0;
  }

  .catalog-page-shell {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0;
    margin-right: 0;
  }
}

.product-list-container {
  display: grid;
  gap: 2rem;
  position: relative;
  width: 100% !important;
  max-width: 100%;
  overflow: visible;
}

@media (min-width: 992px) {
  .product-list-container {
    grid-template-columns: 280px 1fr;
  }
}

@media (max-width: 400px) {
  .product-list-container {
    gap: 1rem;
    padding: 0;
  }
}

/* Products section responsive */
.products-section {
  position: relative;
  width: 100%;
  overflow: visible;
  background: #000000;
}

/* Products grid responsive */
.products-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }
}

@media (max-width: 576px) {
  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 2fr));
    gap: 0.75rem;
  }
}

@media (max-width: 400px) {
  .products-grid {
    grid-template-columns: 2fr;
    gap: 1rem;
    padding: 0;
  }
  
  .product-card {
    margin: 0 auto;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
}

.product-list-container {
  display: grid;
  grid-template-columns: 400px 2fr;
  gap: 2rem;
  padding-top: 140px;
  transition: all 0.3s ease;
}

.sidebar {
  height: fit-content;
  margin-top: 0;
  position: sticky;
  top: 100px;
  transition: all 0.3s ease;
}

.filter-section {
  background: #000000;
  border-radius: 16px;
  padding: 1.5rem;
  margin-top: 0;
  transition: all 0.3s ease;
}

button{
  font-weight: 500;
}

.filter-section h1 {
  margin-bottom: 0;
}

.filter-section > p {
  opacity: 0.8;
  margin-bottom: 1.5rem;
}

.categories-section {
  margin-top: 1rem;
  border-top: 1px solid #222222;
  padding-top: 1.5rem;
}

.catalog-mobile-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e8e8e8;
}

.catalog-mobile-bar-title {
  font-weight: 600;
  font-size: 1rem;
  color: #ffffff;
}

@media (min-width: 992px) {
  .catalog-mobile-bar {
    display: none !important;
  }
}

.catalog-nav-item {
  position: relative;
  border-bottom: 1px solid #222222;
  transition: background 0.2s ease;
}

.catalog-nav-item.is-open {
  background: #0f0f0f;
}

.catalog-nav-item.has-active .catalog-nav-trigger {
  color: var(--primary-color);
}

.catalog-nav-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
  text-align: left;
}

.catalog-nav-trigger-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: #b8b8b8;
  margin-left: auto;
  margin-right: 0.35rem;
}

.catalog-submenu {
  padding: 0 0 0.85rem 0;
}

.catalog-submenu-scroll {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 6px;
  margin-right: -2px;
}

.subtle-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.subtle-scroll::-webkit-scrollbar {
  width: 5px;
}

.subtle-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.subtle-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
  border-radius: 10px;
}

.subtle-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.28);
}

.catalog-submenu-heading {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #b8b8b8;
  margin: 0.35rem 0 0.4rem;
  font-weight: 600;
}

.catalog-submenu-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.6rem;
  margin-bottom: 2px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-size: 0.86rem;
  color: #f2f2f2;
  cursor: pointer;
  text-align: left;
  font-weight: 400;
  transition: background 0.15s ease, color 0.15s ease;
}

.catalog-submenu-row:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.catalog-submenu-row.active {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  font-weight: 600;
}

.catalog-submenu-row:disabled,
.catalog-submenu-row.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.catalog-submenu-count {
  font-size: 0.78rem;
  color: #b8b8b8;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.catalog-submenu-empty {
  padding: 0.5rem 0.35rem;
}

.catalog-sort-btn {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dddddd;
  border-radius: 10px;
  background: #ffffff;
  color: #111111;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
}

.catalog-sort-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.catalog-sort-btn i {
  font-size: 1.05rem;
}

.filter-badge-brand {
  background: #f5f2ff;
  border: 1px solid #e4dcf5;
}

.category-groups {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-group {
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  border-bottom: 1px solid #e8e8e8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.category-group:hover {
  background: #f8f8f8;
}

.group-header {
  padding: 0.9rem 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.group-header:hover {
  background: transparent;
}

.group-header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #111111;
}

.single-category {
  padding: 0.65rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: transparent;
  border: none;
  color: #333333;
  text-align: left;
  font-weight: 400;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.category-icon {
  color: var(--primary-color);
  font-size: 0.9rem;
  width: 20px;
  text-align: center;
}

.category-count {
  font-size: 0.82rem;
  color: #888888;
  margin-left: 0.25rem;
  font-weight: 400;
}

.fa-chevron-down {
  transition: transform 0.3s ease;
  font-size: 0.75rem;
  color: #555555;
}

.fa-chevron-down.rotate {
  transform: rotate(-180deg);
}

.group-content {
  max-height: 0;
  overflow-y: auto;
  transition: max-height 0.3s ease-in-out;
  background: transparent;
}

.group-content.show {
  max-height: 500px;
}

.subcategory-item {
  padding: 0.85rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: transparent;
  border: none;
  color: #444444;
  text-align: left;
  cursor: pointer;
  font-size: 0.88rem;
  transition: all 0.2s ease;
  position: relative;
  border-radius: 6px;
}

.subcategory-item::before {
  display: none;
}

.subcategory-item:hover {
  color: #111111;
  background: rgba(0, 0, 0, 0.03);
  padding-left: 1.8rem;
}

.subcategory-item:hover::before {
  display: none;
}

.subcategory-item.active {
  background: transparent;
  color: #111111;
  font-weight: 500;
}

.subcategory-item.active::before {
  display: none;
}

.subcategory-count {
  font-size: 0.8rem;
  color: #999999;
  transition: all 0.2s ease;
}

.subcategory-item:hover .subcategory-count,
.subcategory-item.active .subcategory-count {
  color: #555555;
}

/* Loading state styles for categories */
.subcategory-item.loading,
.single-category.loading {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: rgba(45, 74, 231, 0.06);
}

.subcategory-item.loading:hover,
.single-category.loading:hover {
  background-color: rgba(45, 74, 231, 0.06);
  transform: none;
}

.subcategory-item:disabled,
.single-category:disabled {
  pointer-events: none;
}

/* Search box enhancements */
.search-box {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #c5c5c5;
  font-size: 0.9rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.6rem;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  background: #101010;
  color: #ffffff;
  font-size: 0.88rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  border-color: #666666;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.08);
  outline: none;
  background: #0a0a0a;
}

.search-input::placeholder {
  color: #888888;
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: #1f1f1f;
  border: none;
  color: #d0d0d0;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-search:hover {
  background: #2b2b2b;
  color: #ffffff;
}

.active-filters {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #f5f5f5;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.active-filters-title {
  color: #111111;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Enhanced price range slider */
.price-slider {
  position: relative;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  margin: 2rem 0 1.5rem;
}

.price-slider input[type="range"] {
  position: absolute;
  width: 100%;
  height: 100%;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
  outline: none;
}

.price-slider input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 18px;
  width: 18px;
  border-radius: 50%;
  background: var(--primary-color);
  cursor: pointer;
  pointer-events: auto;
  margin-top: -6px;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.price-slider input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.price-slider input[type="range"]::-moz-range-thumb {
  height: 18px;
  width: 18px;
  border-radius: 50%;
  background: var(--primary-color);
  cursor: pointer;
  pointer-events: auto;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.price-slider input[type="range"]::-moz-range-thumb:hover {
  transform: scale(1.1);
}

.price-range-progress {
  position: absolute;
  height: 100%;
  background: var(--primary-color);
  border-radius: 3px;
  pointer-events: none;
}

/* Enhanced price inputs */
.price-inputs {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.price-input {
  position: relative;
  flex: 1;
}

.price-symbol {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #888888;
  font-size: 0.9rem;
}

.price-input input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 1.75rem;
  background: #f7f7f7;
  border: 1px solid #dddddd;
  border-radius: 8px;
  color: #111111;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.price-input input:focus {
  border-color: var(--primary-color);
  outline: none;
}

/* Filter tags styling */
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f0f0f0;
  border: 1px solid #dddddd;
  color: #333333;
  border-radius: 20px;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.filter-tag:hover {
  background: #e5e5e5;
}

.filter-tag i {
  color: #888888;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tag i:hover {
  color: var(--primary-color);
}

/* Enhanced products grid */
.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  padding: 1rem 0;
  min-height: 400px;
  width: 100%;
}

.product-item {
  transition: all 0.3s ease;
  width: 100%;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s forwards;
}

.product-skeleton-item {
  width: 100%;
  min-width: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Staggered animation for products */
.product-item:nth-child(1) { animation-delay: 0.1s; }
.product-item:nth-child(2) { animation-delay: 0.15s; }
.product-item:nth-child(3) { animation-delay: 0.2s; }
.product-item:nth-child(4) { animation-delay: 0.25s; }
.product-item:nth-child(5) { animation-delay: 0.3s; }
.product-item:nth-child(6) { animation-delay: 0.35s; }
.product-item:nth-child(7) { animation-delay: 0.4s; }
.product-item:nth-child(8) { animation-delay: 0.45s; }
.product-item:nth-child(9) { animation-delay: 0.5s; }

/* Enhanced products header */
.products-header {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  border-radius: 12px;
}

.products-header-mobile {
  margin-bottom: 0;
  padding: 0;
}
.products-header-mobile .sidebar-header-block {
  margin-bottom: 0;
  padding: 0;
}

.products-header-mobile h1{
  font-size: 1.5rem;
}

.products-header-mobile .products-found-text {
  font-size: 0.9rem;
  color: #666666;
  line-height: 1.35;
}

/* Franja horizontal de categorías (móvil / < lg) */
.catalog-mobile-category-rail {
  margin-bottom: 1rem;
  width: 100%;
  min-width: 0;
}

.catalog-mobile-category-scroll {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.15rem 0 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.catalog-mobile-category-scroll::-webkit-scrollbar {
  height: 4px;
}

.catalog-mobile-category-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.catalog-mobile-category-pill {
  flex: 0 0 auto;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  border: 1px solid #dddddd;
  background: #ffffff;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #333333;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.catalog-mobile-category-pill--icon {
  width: 2.5rem;
  padding: 0.5rem 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.catalog-mobile-category-pill--icon i {
  font-size: 0.95rem;
}

.catalog-mobile-category-pill:hover {
  border-color: #bbbbbb;
  background: #fafafa;
}

.catalog-mobile-category-pill.active {
  border-color: var(--primary-color);
  background: rgba(var(--primary-color-rgb), 0.1);
  color: var(--primary-color);
}

.catalog-mobile-category-skeleton {
  display: flex;
  gap: 0.5rem;
  overflow: hidden;
  padding: 0.15rem 0 0.5rem;
}

.catalog-mobile-category-skeleton-pill {
  flex: 0 0 auto;
  width: 4.5rem;
  height: 2rem;
  border-radius: 999px;
  background: linear-gradient(90deg, #151515 0%, #262626 50%, #151515 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.products-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.products-count {
  color: #555555;
  font-size: 0.95rem;
  margin: 0;
  opacity: 0.9;
}

.sort-wrapper {
  min-width: 220px;
}

.sort-select {
  width: 100%;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.sort-select:focus {
  border-color: var(--primary-color);
  outline: none;
  box-shadow: 0 0 0 2px rgba(45, 74, 231, 0.12);
}

.sort-select option {
  background: #ffffff;
  color: #111111;
  padding: 0.5rem;
}

/* Mobile Styles */
.filter-toggle-btn {
  width: 100%;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 12px;
  color: #111111;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.filter-toggle-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.filter-toggle-btn i {
  font-size: 1rem;
  color: var(--primary-color);
}

.filter-count {
  background: var(--primary-color);
  color: var(--white-color);
  font-size: 0.75rem;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-left: 0.5rem;
}

/* Enhanced mobile sidebar */
@media (max-width: 768px) {
  /*
   * No usar display:none en .sidebar: en viewport ≤768px anula el panel de filtros
   * aunque .mobile-active aplique transform (definido en el bloque max-width:991px).
   */

  .mobile-filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e8e8e8;
  }

  .mobile-filter-header h3 {
    color: #111111;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  .close-filters {
    background: #f0f0f0;
    border: none;
    color: #333333;
    font-size: 1rem;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-filters:hover {
    background: #e0e0e0;
  }
  
  .filter-section {
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: none;
    flex: 1;
    margin-bottom: 0;
    overflow-y: auto;
  }
  
  /* Add some space at the bottom for mobile devices */
  .categories-section {
    padding-bottom: 80px;
  }
}

/* Sidebar responsive improvements */
.sidebar {
  background: #000000;
  border-radius: 12px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 1rem;
  transition: all 0.3s ease;
}

@media (max-width: 991px) {
  /*
   * El sidebar es fixed: si el padre es grid con 2 hijos, igual se reserva una fila/celda
   * y queda un hueco enorme. display:block hace que el fixed no consuma altura del flujo.
   */
  .product-list-container {
    display: block;
    width: 100%;
    max-width: 100%;
    padding-top: 3.5rem;
  }

  .sidebar.catalog-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    margin: 0;
    z-index: 1050;
    transform: translateX(-100%);
    opacity: 0;
    padding: 1.5rem;
    padding-bottom: calc(1.5rem + 88px);
    border-radius: 0;
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    pointer-events: none;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  }

  .sidebar.catalog-sidebar.mobile-active {
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
    background: #000000;
  }
}

/* Product card responsive improvements */
.product-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  overflow: hidden;
  box-sizing: border-box;
}

@media (max-width: 400px) {
  .products-grid {
  display: grid;
  grid-template-columns: repeat(1, 2fr) !important;
  width: 100%;
}
  .product-card {
    font-size: 0.9rem;
    margin-left: 0;
    margin-right: 0;
  }

  .product-card img {
    max-width: 100%;
    height: auto;
    object-fit: cover;
  }

  .product-card .product-title {
    font-size: 1rem;
  }

  .product-card .product-price {
    font-size: 1.1rem;
  }
}

/* Mobile filter header improvements */
.mobile-filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 1rem;
}

.mobile-filter-header h3 {
  font-size: 1.2rem;
  color: #111111;
  margin: 0;
}

.close-filters {
  background: #f0f0f0;
  border: none;
  color: #333333;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-filters:hover {
  background: #e0e0e0;
}

/* Mobile filter toggle button improvements */
.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  color: #111111;
  border: 1px solid #dddddd;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

@media (max-width: 400px) {
  .filter-toggle-btn {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
}

/* Mobile filter actions improvements */
.mobile-filter-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
  background: #000000;
  border-top: 1px solid #222222;
  display: flex;
  gap: 1rem;
  z-index: 1051;
  box-sizing: border-box;
}

.mobile-filter-actions button {
  flex: 1;
  padding: 0.75rem;
  font-size: 0.9rem;
}

/* Overlay for mobile filters */
.mobile-filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1049;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(3px);
}

.mobile-filter-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Enhanced pagination styles */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  bottom: -20px;
  gap: 0.5rem;
  margin: 2rem 0;
  width: 100%;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0.5rem;
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #333333;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #ffffff;
  transform: translateY(-2px);
}

.pagination-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #ffffff;
  pointer-events: none;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f5f5f5;
}

.pagination-btn i {
  font-size: 0.875rem;
}

@media (max-width: 576px) {
  .pagination-container {
    gap: 0.25rem;
  }
  
  .pagination-btn {
    min-width: 36px;
    height: 36px;
    padding: 0.25rem;
    font-size: 0.875rem;
  }
}

/* Sale filter style */
.sale-filter {
  margin-top: 15px;
}

.sale-filter .form-check-input {
  width: 1.5em;
  height: 1.5em;
  cursor: pointer;
  background-color: #ffffff;
  border: 1.5px solid #cccccc;
}

.sale-filter .form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-dark-color);
}

.sale-filter .form-check-label {
  cursor: pointer;
  font-size: 1rem;
  padding-left: 0.5rem;
  display: flex;
  align-items: center;
}

.sale-filter .text-danger {
  font-size: 1.1rem;
}

/* Estilo especial para la categoría de ofertas */
.category-group .single-category[class*="active"] .category-icon {
  color: var(--white-color);
}

.sidebar {
  background: #000000;
  border-radius: 12px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 1rem;
  transition: all 0.3s ease;
}

/* New styles for header elements */
.products-header {
  padding: 1rem;
  border-radius: 12px;
}

.form-select {
  min-width: 150px;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
}

.form-select:focus {
  box-shadow: none;
  border-color: var(--primary-color);
}

/* Applied Filters Styles */
.applied-filters {
  padding: 0.5rem 0 0.75rem;
  border-bottom: 1px solid #222222;
  margin-bottom: 0.5rem;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: var(--primary-color);
  color: var(--white-color);
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
}

.filter-badge:hover {
  background: var(--primary-color-dark, var(--primary-dark));
  transform: translateY(-1px);
}

.filter-badge-sale {
  background: linear-gradient(45deg, #dc3545, #c82333);
}

.filter-badge-sale:hover {
  background: linear-gradient(45deg, #c82333, #bd2130);
}

.filter-badge-search {
  background: #6c757d;
  text-transform: none;
  font-weight: 500;
}

.filter-badge-search:hover {
  background: #5a6268;
}

.filter-remove-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: var(--white-color);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 0.65rem;
}

.filter-remove-btn:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.filter-remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 576px) {
  .applied-filters {
    padding: 0.35rem 0 0.6rem;
  }
  
  .filter-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.6rem;
  }
}

/* ===== SIDEBAR HEADER ===== */
.sidebar-header-block {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #222222;
}

.catalog-heading {
  font-size: 1.9rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
  margin: 0 !important;
  line-height: 1.1;
}

.products-found-text {
  font-size: 0.82rem;
  color: #b8b8b8;
  margin: 0.35rem 0 0;
  letter-spacing: 0.1px;
}

.result-count {
  font-weight: 700;
  color: #ffffff;
}

.clear-all-btn {
  background: transparent;
  border: 1.5px solid #333333;
  color: #d0d0d0;
  font-size: 0.75rem;
  padding: 0.25rem 0.65rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 500;
  line-height: 1.5;
}

.clear-all-btn:hover {
  background: #1a1a1a;
  border-color: #555555;
  color: #ffffff;
}

select{
  font-weight: 500;
}

/* ===== CUSTOM CHECKBOX ===== */
.sub-check-box {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border: 1.5px solid #d0d0d0;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: all 0.2s ease;
  font-size: 0.52rem;
  color: #fff;
}

.subcategory-item:hover .sub-check-box {
  border-color: #666;
  background: #fafafa;
}

.subcategory-item.active .sub-check-box {
  background: #111;
  border-color: #111;
}

/* ===== SKELETON SHIMMER ===== */
.category-skeleton {
  padding-top: 0.25rem;
}

.skeleton-group {
  border-bottom: 1px solid #222222;
}

.skeleton-nav-row {
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.skeleton-nav-label {
  width: 54%;
  height: 1rem;
  border-radius: 5px;
}

.skeleton-nav-meta {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.skeleton-nav-count {
  width: 16px;
  height: 0.85rem;
  border-radius: 4px;
}

.skeleton-nav-chevron {
  width: 11px;
  height: 11px;
  border-radius: 50%;
}

.skeleton-subcategories {
  padding: 0 0 0.75rem 0.65rem;
}

.skeleton-sub-row {
  min-height: 34px;
  display: grid;
  grid-template-columns: 18px 1fr 24px;
  align-items: center;
  gap: 0.55rem;
  padding-right: 0.15rem;
}

.skeleton-sub-check {
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.skeleton-sub-label {
  width: 72%;
  height: 0.82rem;
  border-radius: 5px;
}

.skeleton-sub-count {
  width: 22px;
  height: 0.75rem;
  border-radius: 4px;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #171717 0%, #252525 50%, #171717 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.35s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ===== SEARCH IMPROVEMENTS ===== */
.search-input {
  border-bottom-color: #ccc !important;
  transition: border-color 0.25s ease, box-shadow 0.25s ease !important;
}

.search-input:focus {
  border-color: #555 !important;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.12) !important;
}

/* ===== CATEGORY GROUP IMPROVEMENTS ===== */
.category-group:last-child {
  border-bottom: none;
}

.group-header {
  padding: 0.85rem;
}

.group-header-content .fw-normal {
  font-size: 0.88rem;
  font-weight: 500 !important;
  letter-spacing: 0.3px;
}

.subcategory-count {
  font-size: 0.75rem;
  color: #bbb;
  flex-shrink: 0;
  min-width: 18px;
  text-align: right;
}

.subcategory-item.active .subcategory-count {
  color: #777;
}

@media (max-width: 991px) {
  .product-list-container {
    gap: 0;
  }

  .products-section {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .products-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
}

@media (max-width: 576px) {
  .product-list-container {
    padding-top: 1.5rem;
  }

  .products-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (min-width: 992px) {
  .product-list-container {
    padding-top: 8rem;
  }
}
</style>
